/** @jsx jsx */

import { ReactNode, SyntheticEvent, MouseEvent } from 'react';
import { css, jsx } from '@emotion/core';

import _ from 'lodash';
import { transparentize } from 'polished';

import { addFromUrl } from '@zapier/url-utils';
import { createKeyHandler } from '@zapier/common-utils';
import { useLink } from '@zapier/common-hooks';
import { Colors, Duration, ColorName } from '@zapier/theme';

type TrackDataType = string | ((options: {}) => void) | void;

type UnderlineType = 'always' | 'hover' | 'never';

type MouseCursor = 'pointer' | 'not-allowed' | 'default' | 'inherit';

type Props = {
  /** Page anchor */
  anchor?: string;
  /** aria-label attribute */
  ariaLabel?: string;
  /** Component children */
  children: ReactNode;
  /** Link Color */
  color: string;
  /** The type of cursor that displays over the link */
  cursor?: MouseCursor;
  /** Link URL */
  href?: string;
  /** Color on hover */
  hoverColor?: string;
  /** Disables link */
  isDisabled: boolean;
  /** Do not allow text breaking */
  isUnbreakable: boolean;
  /** Handle clicking */
  onClick?: (e: MouseEvent<HTMLElement>) => void;
  /** Track when link is clicked */
  onTrack?: (option: string, track: TrackDataType) => void;
  /** Keep same scroll position when link is clicked */
  preserveScrollPosition?: boolean;
  /** For client side routing */
  routeTo?: (
    route: string,
    options?: { event: SyntheticEvent<HTMLElement>; exit?: boolean }
  ) => void;
  /** Passes current URL as from (client side routing) */
  shouldPassCurrentUrl: boolean;
  /** Refreshes page when link is clicked (client side routing) */
  shouldRefreshPage: boolean;
  /** Scrolls to the top when link is clicked (client side routing) */
  shouldScrollToTop: boolean;
  /** Stops event propagation when link is clicked */
  stopPropagation: boolean;
  /** Link target */
  target?: string;
  /** Tracking Data (function or string) */
  trackData?: TrackDataType;
  /** Tracking Message */
  trackMessage?: string;
  /** `always`: Always show text-decoration underline
      `never`: Never show underline
      `hover`: only show underline on hover
   */
  underline: UnderlineType;
  /** Optional ID to identify the link */
  id?: string;
};

// Array of predicate functions indicating that a `url` cannot be navigated to.
// Predicate should return `true` if the URL *cannot* be navigated to.
const unnavigableUrlPredicates = [(url: string) => url.startsWith('mailto')];
export const isNavigableUrl = (url: string): boolean =>
  unnavigableUrlPredicates.every(predicate => !predicate(url));

const lineTop = '89%';
const lineBottom = '94%';

const multilineUnderline = (color: string) => ({
  backgroundImage: `linear-gradient(transparent ${lineTop}, ${color} ${lineTop}, ${color} ${lineBottom}, transparent ${lineBottom})`,
  backgroundRepeat: 'no-repeat',
  backgroundSize: '0% 100%',
  transition: `background-size ${Duration.short} ease-in-out`,
  width: 'calc(100%)',
  '&:hover, &:focus': {
    backgroundSize: '100% 100%',
    textDecoration: 'none',
  },
});

const getCss = ({
  color,
  hoverColor,
  isDisabled,
  isUnbreakable,
  underline,
  cursor,
}: Props) => {
  const colorBase = Colors[color as ColorName] || color;
  return css(
    {
      color: colorBase,
      cursor: cursor || 'pointer',
      outline: 'none',
      textDecoration: 'none',
      transition: `color ${Duration.short} ease-in-out`,
    },
    hoverColor && {
      '&:hover': {
        color: hoverColor,
      },
    },
    isUnbreakable && {
      whiteSpace: 'nowrap',
    },
    underline === 'always'
      ? {
          textDecoration: 'underline',
        }
      : isDisabled || underline === 'never'
      ? {
          ':hover': {
            textDecoration: 'none',
          },
        }
      : multilineUnderline(hoverColor || colorBase),
    isDisabled && {
      color: transparentize(0.4, colorBase),
      cursor: cursor || 'default',
      textDecoration: 'none',
      '&:hover': {
        color: transparentize(0.4, colorBase),
        textDecoration: 'none',
      },
    }
  );
};

const getWindowLocation = () =>
  typeof window !== 'undefined' ? window.location.href : '';

const getCompleteDestinationUrl = (url?: string, anchor?: string) =>
  anchor && url ? `${url}${anchor}` : url;

const handlePageScrolling = (
  preserveScrollPosition: boolean | undefined,
  shouldScrollToTop: boolean
) => {
  const shouldScroll = shouldScrollToTop || preserveScrollPosition;

  const documentEl = _.get(document, 'documentElement');
  if (shouldScroll && documentEl) {
    const scrollValue = preserveScrollPosition ? documentEl.scrollTop : 0;
    requestAnimationFrame(() => {
      documentEl.scrollTop = scrollValue;
    });
  }
};

/**
 * Accessible Link
 * * **NOTE**: for this this be connected to redux-router-kit the `routeTo` function
 * must be passed in or the LinkProvider must be wrapped around it
 */
const Link = (props: Props) => {
  // Use LinkProvider to get route to or override with props.routeTo
  const { routeTo: providerRouteTo } = useLink();
  const route = props.routeTo || providerRouteTo;

  const finishClick = (event: SyntheticEvent<HTMLElement>) => {
    event.preventDefault();

    if (props.stopPropagation) {
      event.stopPropagation();
    }
  };

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    // Handle onClick if given
    if (props.onClick) {
      props.onClick(event);
    }

    // Do tracking if required
    if (props.onTrack && props.trackMessage) {
      const data =
        typeof props.trackData === 'function'
          ? props.trackData(props)
          : props.trackData;
      props.onTrack(props.trackMessage, data);
    }

    // If this link goes to a target, skip frontend routing.
    if (props.target) return;

    handlePageScrolling(props.preserveScrollPosition, props.shouldScrollToTop);

    // Handle routeTo if given
    if (
      typeof route === 'function' &&
      props.href &&
      isNavigableUrl(props.href)
    ) {
      const routeToLink = getCompleteDestinationUrl(
        props.shouldPassCurrentUrl
          ? addFromUrl(props.href, getWindowLocation())
          : props.href,
        props.anchor
      );
      route(routeToLink || '', {
        event,
        exit: props.shouldRefreshPage,
      });
    }

    if (props.href && route) {
      // Finish the click event
      finishClick(event);
    }
  };

  const url = getCompleteDestinationUrl(
    props.target && props.shouldPassCurrentUrl
      ? addFromUrl(props.href, getWindowLocation())
      : props.href,
    props.anchor
  );

  const rel = props.target && 'noopener noreferrer';

  const linkCss = getCss(props);

  const commonProps = {
    'aria-disabled': props.isDisabled,
    'aria-label': props.ariaLabel,
    css: linkCss,
    disabled: props.isDisabled,
    rel,
    tabIndex: 0,
    target: props.target,
    id: props.id,
  };

  if (props.onClick && !props.href) {
    return (
      <a
        {...commonProps}
        onClick={(!props.isDisabled && handleClick) || _.noop}
        onKeyDown={createKeyHandler(
          ['enter'],
          props.isDisabled ? _.noop : handleClick
        )}
        role="button"
        tabIndex={0}
      >
        {props.children}
      </a>
    );
  }

  // If this is disabled only run the finishClick and not entire click event
  const onClick = props.isDisabled ? finishClick : handleClick;

  return (
    <a {...commonProps} href={url} onClick={onClick}>
      {props.children}
    </a>
  );
};

Link.defaultProps = {
  color: Colors.heroine,
  isDisabled: false,
  isUnbreakable: false,
  shouldPassCurrentUrl: true,
  shouldRefreshPage: false,
  shouldScrollToTop: false,
  stopPropagation: false,
  underline: 'hover',
};

export default Link;
